import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, Shield, Coins, Star } from 'lucide-react';
import { SITE, IMAGES, SERVICES, SUPPORT_PILLARS, TESTIMONIALS, VALUES } from '../mock';
import Newsletter from '../components/Newsletter';
import ServicesGrid from '../components/ServicesGrid';
import SEO, { ORGANIZATION_SCHEMA } from '../components/SEO';

export default function Home() {
  return (
    <div className="relative">
      <SEO
        path="/"
        title="Moonshine Solutions GmbH – Vermögensaufbau & finanzieller Erfolg"
        description="Inflationsgeschützter & steueroptimierter Vermögensaufbau in der Schweiz. Immobilien, KI-Trading, Vorsorge und Edelmetalle – begleitet von Pascal Schwab."
        schema={ORGANIZATION_SCHEMA}
      />
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full bg-[#d4b574]/8 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-[#b8945a]/6 blur-[100px]" />
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="anim-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#d4b574]/10 border border-[#d4b574]/25 text-[11px] uppercase tracking-[0.24em] text-[#e8d5a8] mb-7">
              <Sparkles size={12} /> {SITE.brandFull}
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] text-[#f5efe3]">
              Deine Zukunft.<br />
              Dein Vermögen.<br />
              <span className="text-gold-grad italic">Deine Träume.</span>
            </h1>
            <p className="mt-7 text-lg text-[#9a9285] leading-relaxed max-w-xl">
              Mit unseren bewährten Strategien und Konzepten unterstützen wir dich auf deinem Weg zum <span className="text-[#e8d5a8]">finanziellen Erfolg</span> – inflationsgeschützt, steueroptimiert und nachhaltig.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={SITE.vorsorgecheckUrl}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors"
              >
                Mach jetzt deinen kostenlosen Finanzcheck
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/angebot"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-[#cfc6b5] hover:border-[#d4b574]/40 hover:text-[#e8d5a8] transition-all"
              >
                Unsere Angebote
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-8 text-xs uppercase tracking-[0.22em] text-[#7a7060]">
              <div className="flex items-center gap-2"><TrendingUp size={14} className="text-[#d4b574]" /> Wachstum</div>
              <div className="flex items-center gap-2"><Shield size={14} className="text-[#d4b574]" /> Sicherheit</div>
              <div className="flex items-center gap-2"><Coins size={14} className="text-[#d4b574]" /> Vermögen</div>
            </div>
          </div>

          <div className="relative anim-fade-up" style={{ animationDelay: '120ms' }}>
            <div className="absolute -inset-6 bg-gradient-to-tr from-[#d4b574]/15 via-transparent to-[#d4b574]/10 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#d4b574]/15">
              <img src={IMAGES.hero} alt="Pascal Schwab" className="w-full h-auto object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10"><div className="divider-gold" /></div>

      {/* WELCOME */}
      <section className="py-20 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-4">Willkommen</div>
          <h2 className="font-serif-display text-4xl md:text-6xl text-[#f5efe3]">
            Willkommen in unserer <span className="text-gold-grad italic">Welt.</span>
          </h2>
          <p className="mt-7 text-lg text-[#9a9285] leading-relaxed">
            Wachstum und finanzielle Freiheit. Die Welt des passiven Einkommens, Vermögensaufbaus und erfolgreicher Investitionen in Immobilien, KI-Trading und Edelmetallen – begleitet durch das Fachwissen von Pascal Schwab.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section id="angebote" className="py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Unsere Angebote</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3] max-w-2xl leading-tight">
                Vier Wege zu deinem <span className="italic text-gold-grad">finanziellen Erfolg</span>
              </h2>
            </div>
            <Link to="/angebot" className="inline-flex items-center gap-2 text-sm text-[#d4b574] hover:gap-3 transition-all">
              Alle Angebote ansehen <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <Link
                key={s.id}
                to={s.href}
                className="card-glow group relative overflow-hidden rounded-2xl border border-white/8 bg-[#0e0e0c]"
              >
                <div className="grid md:grid-cols-2 h-full">
                  <div className="relative h-56 md:h-auto overflow-hidden">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e0c] via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#0e0e0c]" />
                  </div>
                  <div className="p-7 flex flex-col justify-center">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#d4b574] mb-3">0{i + 1}</div>
                    <h3 className="font-serif-display text-2xl md:text-[26px] text-[#f5efe3] group-hover:text-[#e8d5a8] transition-colors leading-snug">{s.title}</h3>
                    <p className="mt-3 text-sm text-[#9a9285] leading-relaxed line-clamp-3">{s.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#d4b574] group-hover:gap-3 transition-all">
                      Mehr dazu <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 text-center">
            <h3 className="font-serif-display text-3xl md:text-4xl text-[#f5efe3]">und vieles mehr…</h3>
            <Link to="/kontakt" className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[#d4b574]/40 text-[#e8d5a8] hover:bg-[#d4b574] hover:text-[#0a0a0c] transition-all">
              Interesse geweckt? Klick hier <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SUPPORT PILLARS */}
      <section className="py-24 px-6 lg:px-10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Unterstützung</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">
              Wir können Dich wie folgt <span className="italic text-gold-grad">unterstützen</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {SUPPORT_PILLARS.map((p, i) => (
              <div key={i} className="card-glow rounded-2xl border border-white/8 bg-[#0e0e0c] p-8">
                <div className="text-[#d4b574] text-3xl font-serif-display">0{i + 1}</div>
                <h3 className="font-serif-display text-2xl text-[#f5efe3] mt-4">{p.title}</h3>
                <p className="text-sm text-[#9a9285] leading-relaxed mt-3">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#d4b574]/15 to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-[#d4b574]/15">
              <img src={IMAGES.pascal} alt="Pascal Schwab" className="w-full h-auto" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Über uns</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3] leading-tight">
              Pascal Schwab – <span className="italic text-gold-grad">Geschäftsführer</span><br /> Moonshine Solutions GmbH
            </h2>
            <p className="mt-6 text-[#9a9285] leading-relaxed">
              Ich bin dein persönlicher Experte für inflationsgeschützten und steueroptimierten <span className="text-[#e8d5a8]">Vermögensaufbau</span>. Als Familienvater weiß ich genau, welche finanziellen Herausforderungen das Leben mit sich bringen kann.
            </p>
            <blockquote className="mt-7 pl-6 border-l-2 border-[#d4b574]/50 font-serif-display italic text-xl text-[#e8d5a8] leading-snug">
              „Alles was ich will ist mein eigenes Leben zu gestalten und dabei anderen helfen, ihr volles Potenzial zu entfalten.“
            </blockquote>
            <p className="mt-6 text-[#9a9285] leading-relaxed">
              Deshalb biete ich dir <span className="text-[#e8d5a8]">massgeschneiderte Lösungen</span> für deine individuelle Situation – ob <Link to="/immobilien" className="text-[#e8d5a8] link-underline">Immobilien</Link>, <Link to="/alphalane" className="text-[#e8d5a8] link-underline">Alphalane AI Trading</Link>, <Link to="/vorsorge" className="text-[#e8d5a8] link-underline">Vorsorge</Link> oder <Link to="/edelmetalle" className="text-[#e8d5a8] link-underline">Edelmetalle</Link>. Das Ziel: gemeinsam finanzielle Stabilität und Wohlstand zu erreichen.
            </p>
            <Link to="/ueber-uns" className="mt-8 inline-flex items-center gap-2 text-sm text-[#d4b574] hover:gap-3 transition-all">
              Mehr über uns <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="py-16 px-6 lg:px-10 border-y border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-6">
          {VALUES.map((v, i) => (
            <div key={i} className="text-center">
              <div className="font-serif-display text-3xl text-gold-grad">0{i + 1}</div>
              <div className="mt-2 text-sm uppercase tracking-[0.22em] text-[#e8d5a8]">{v.title}</div>
              <div className="mt-2 text-xs text-[#9a9285] leading-relaxed">{v.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Kundenfeedback</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3]">
              Was unsere Kunden <span className="italic text-gold-grad">sagen</span>
            </h2>
            <div className="mt-6 inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#0e0e0c] border border-[#00b67a]/30">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-[#00b67a]" fill="#00b67a" />
                ))}
              </div>
              <div className="text-xs text-[#cfc6b5]">
                <span className="text-[#e8d5a8] font-medium">Trustpilot</span> · Verifizierte Bewertungen
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="card-glow rounded-2xl border border-white/8 bg-[#0e0e0c] p-7 flex flex-col">
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(t.rating || 5)].map((_, j) => (
                    <Star key={j} size={14} className="text-[#00b67a]" fill="#00b67a" />
                  ))}
                </div>
                <p className="text-[#cfc6b5] leading-relaxed flex-1">{t.text}</p>
                <div className="mt-6 pt-5 border-t border-white/8">
                  <div className="font-serif-display text-lg text-[#e8d5a8]">{t.name}</div>
                  <div className="text-xs text-[#7a7060] mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href={SITE.trustpilotUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#d4b574] link-underline">
              Alle Bewertungen auf Trustpilot ansehen <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* CAREER CTA */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto rounded-3xl overflow-hidden relative border border-[#d4b574]/15">
          <div className="grid md:grid-cols-2">
            <div className="p-10 md:p-16 bg-[#0e0e0c]">
              <div className="text-xs uppercase tracking-[0.28em] text-[#d4b574] mb-3">Karriere</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5efe3] leading-tight">
                Zielstrebige <span className="italic text-gold-grad">Visionäre</span> gesucht!
              </h2>
              <p className="mt-6 text-[#9a9285] leading-relaxed">
                Become part of a success story! Wir suchen ehrgeizige Macher, die unsere Leidenschaft teilen und in einer dynamischen Branche durchstarten möchten – innovative Karrieremöglichkeiten, in denen deine Fähigkeiten zählen.
              </p>
              <a href={SITE.karriereUrl} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#d4b574] text-[#0a0a0c] font-medium hover:bg-[#e8d5a8] transition-colors">
                Lerne uns kennen <ArrowRight size={16} />
              </a>
            </div>
            <div className="relative min-h-[300px]">
              <img src={IMAGES.careerBg} alt="Karriere" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0e0e0c]/60" />
            </div>
          </div>
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
